import {
  buildConstantNode,
  createInlineNodes,
  buildFunctionNode,
  buildOperatorSymbolNode,
  buildSymbolNode,
  buildVariableNode,
} from "@corca/data";

import { libraryRootNode } from "@/components/suggestions/utils/libraryItem";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";

import type {
  SingleSuggestion,
  Suggestion,
  SuggestionNodeType,
  VariableSuggestionProps,
  ConstantSuggestionProps,
  FunctionSuggestionProps,
  GroupSuggestionProps,
  PlaceholderSuggestionProps,
} from "@/components/suggestions/types";
import type { NodeJson, OperatorSymbolNodeProps, SymbolNodeProps } from "@corca/data";

type NodeJsonOfType<T extends SuggestionNodeType> = T extends NodeJson["type"]
  ? Extract<NodeJson, { type: T }>
  : never;

export function suggestionToNodeJson<T extends SuggestionNodeType>(
  suggestion: SingleSuggestion<T>,
): NodeJsonOfType<T> | undefined {
  const { item } = suggestion;

  // If node already exists, convert it to JSON
  if (item.node) {
    return item.node.toJSON() as NodeJsonOfType<T>;
  }

  // Map suggestion to NodeJson based on type
  switch (item.type) {
    case "library": {
      return libraryRootNode(item) as NodeJsonOfType<T> | undefined;
    }

    case "number": {
      return {
        type: "number",
        props: { value: item.symbol },
        children: [] as NodeJson[],
      } as NodeJsonOfType<T>;
    }

    case "plainText": {
      return {
        type: "plainText",
        props: { value: item.symbol },
        children: [] as NodeJson[],
      } as NodeJsonOfType<T>;
    }

    case "symbol": {
      return buildSymbolNode({
        ...item,
        props: item.props as SymbolNodeProps,
      }) as NodeJsonOfType<T>;
    }

    case "variable": {
      return buildVariableNode({
        ...item,
        name: getNodeName(suggestion),
        props: item.props as VariableSuggestionProps | undefined,
      }) as NodeJsonOfType<T>;
    }

    case "constant": {
      return buildConstantNode({
        ...item,
        name: getNodeName(suggestion),
        props: item.props as ConstantSuggestionProps | undefined,
      }) as NodeJsonOfType<T>;
    }

    case "function": {
      return buildFunctionNode({
        ...item,
        name: getNodeName(suggestion),
        props: item.props as FunctionSuggestionProps | undefined,
      }) as NodeJsonOfType<T>;
    }

    case "operator": {
      if (!item.props || !OperatorNodeModel.validateJson(item.props)) {
        console.warn("Invalid operator props for item", item);
        return undefined;
      }

      const requiredChildren = OperatorNodeModel.getRequiredChildrenCount(item.props.operatorType);
      const op = OperatorNodeModel.getOperatorBySymbol(item.symbol);

      return {
        type: "operator",
        props: {
          key: op?.key,
          name: item.symbol,
          ...item.props,
        },
        children: createInlineNodes(requiredChildren),
      } as NodeJsonOfType<T>;
    }

    case "operatorSymbol": {
      return buildOperatorSymbolNode({
        ...item,
        name: item.displayName,
        props: item.props as OperatorSymbolNodeProps | undefined,
      }) as NodeJsonOfType<T>;
    }

    case "group": {
      const groupProps = item.props as GroupSuggestionProps | undefined;

      const matrixProps = {
        rows: groupProps?.rows ?? 1,
        cols: groupProps?.cols ?? 1,
      };

      const children: NodeJson[] = [
        {
          type: "matrix",
          props: matrixProps,
          children: createInlineNodes(matrixProps.rows * matrixProps.cols),
        },
      ];

      return {
        type: "group",
        props: {
          openingSymbol: groupProps?.openingSymbol?.symbol,
          closingSymbol: groupProps?.closingSymbol?.symbol,
          casesSystemEnabled: groupProps?.casesSystemEnabled,
          omitParentheses: groupProps?.omitParentheses,
        },
        children,
      } as NodeJsonOfType<T>;
    }

    case "placeholder": {
      const placeholderProps = item.props as PlaceholderSuggestionProps | undefined;

      return {
        type: "placeholder",
        props: {
          linkId: placeholderProps?.linkId,
        },
        children: createInlineNodes(1),
      } as NodeJsonOfType<T>;
    }

    default:
      console.warn("Unsupported suggestion type:", item.type);
      return undefined;
  }
}

function getNodeName(suggestion: Suggestion) {
  if (suggestion.type !== "single") return "";
  return suggestion.item.isNewNode ? undefined : suggestion.item.displayName;
}
